import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import ArrowDownLineIcon from "@rsuite/icons/ArrowDownLine";
import { DashboardMenu } from "../data/menu";

type SidebarNavProps = {
  onNavigate?: () => void;
  className?: string;
};

function isPathActive(pathname: string, url: string) {
  if (url === "/dashboard") {
    return pathname === "/dashboard" || pathname === "/dashboard/";
  }
  return pathname === url || pathname.startsWith(`${url}/`);
}

function findOpenIndex(pathname: string) {
  const index = DashboardMenu.findIndex((item) => {
    if (item.sub.length === 0) {
      return isPathActive(pathname, item.url);
    }
    return item.sub.some((sub) => isPathActive(pathname, sub.url));
  });
  return index >= 0 ? index : null;
}

const iconClass = "shrink-0 text-[18px] leading-none text-current";

export default function SidebarNav({ onNavigate, className = "" }: SidebarNavProps) {
  const { t } = useTranslation();
  const location = useLocation();
  const [openIndex, setOpenIndex] = useState<number | null>(() =>
    findOpenIndex(location.pathname)
  );

  useEffect(() => {
    setOpenIndex(findOpenIndex(location.pathname));
  }, [location.pathname]);

  const toggleSection = (index: number) => {
    setOpenIndex((current) => (current === index ? null : index));
  };

  return (
    <nav className={`flex flex-col gap-1 p-3 ${className}`} aria-label={t("Menu")}>
      {DashboardMenu.map((item, index) => {
        const hasSub = item.sub.length > 0;
        const isOpen = openIndex === index;
        const sectionActive =
          isPathActive(location.pathname, item.url) ||
          item.sub.some((sub) => isPathActive(location.pathname, sub.url));
        const Icon = item.Icon;

        if (!hasSub) {
          return (
            <NavLink
              key={item.name}
              to={item.url}
              end
              onClick={onNavigate}
              className={({ isActive }) =>
                [
                  "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-white/15 text-white shadow-sm"
                    : "text-white/80 hover:bg-white/10 hover:text-white",
                ].join(" ")
              }
            >
              <Icon className={iconClass} />
              <span className="flex-1 text-start">{t(item.name)}</span>
            </NavLink>
          );
        }

        return (
          <div key={item.name} className="rounded-lg">
            <button
              type="button"
              onClick={() => toggleSection(index)}
              aria-expanded={isOpen}
              className={[
                "flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
                sectionActive
                  ? "bg-white/10 text-white"
                  : "text-white/80 hover:bg-white/10 hover:text-white",
              ].join(" ")}
            >
              <Icon className={iconClass} />
              <span className="flex-1 text-start">{t(item.name)}</span>
              <ArrowDownLineIcon
                className={[
                  "shrink-0 text-[14px] text-white/60 transition-transform duration-200",
                  isOpen ? "rotate-180" : "",
                ].join(" ")}
              />
            </button>

            <div
              className={[
                "grid transition-[grid-template-rows] duration-200 ease-out",
                isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
              ].join(" ")}
            >
              <div className="overflow-hidden">
                <ul className="mt-1 space-y-0.5 border-s border-white/15 ms-4 ps-2 pb-1">
                  {item.sub.map((sub) => {
                    const SubIcon = sub.Icon;
                    return (
                      <li key={sub.url}>
                        <NavLink
                          to={sub.url}
                          onClick={onNavigate}
                          className={({ isActive }) =>
                            [
                              "flex items-center gap-2 rounded-md px-2.5 py-2 text-[13px] transition-colors",
                              isActive
                                ? "bg-white text-slate-900 font-medium"
                                : "text-white/70 hover:bg-white/10 hover:text-white",
                            ].join(" ")
                          }
                        >
                          <SubIcon className="shrink-0 text-[15px] leading-none text-current" />
                          <span className="text-start leading-snug">{t(sub.name)}</span>
                        </NavLink>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        );
      })}
    </nav>
  );
}
